/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/useDisaEntryList.ts
 🗓️ Created: 2025-01-27
 🧩 Description:
    Hook for managing DISA Entries list with DataGrid integration.
────────────────────────────────────────────────────────────────*/

import { useCallback, useEffect } from 'react';
import { useDataGrid, type DataGridFetchResult } from '../../../hooks/useDataGrid';
import { useNotifications } from '../../../hooks/useNotifications';
import { useI18n } from '../../../hooks/useI18n';
import { fetchDisaEntries } from '../api/disaEntriesApi';
import { DisaEntry } from '../types';

interface UseDisaEntryListOptions {
  domainId?: number;
}

/**
 * Hook for DISA Entries list management
 */
export function useDisaEntryList(options: UseDisaEntryListOptions = {}) {
  const { domainId } = options;
  const { showError } = useNotifications();
  const { t } = useI18n();

  const fetchEntries = useCallback(
    async (params: {
      page: number;
      limit: number;
      sortBy: string;
      sortOrder: 'asc' | 'desc';
      search?: string;
    }) => {
      try {
        const response = await fetchDisaEntries({
          ...params,
          domainId
        });
        return {
          data: response.data || [],
          pagination: response.pagination || {
            page: params.page,
            limit: params.limit,
            total: 0,
            totalPages: 0
          }
        } as DataGridFetchResult;
      } catch (error) {
        const message = error instanceof Error ? error.message : t('disaEntries:messages.loadError');
        showError(message);
        throw error;
      }
    },
    [showError, t, domainId]
  );

  const getAvailableColumns = useCallback(() => {
    return [
      {
        key: 'name',
        label: t('disaEntries:columns.name'),
        visible: true,
        sortable: true
      },
      {
        key: 'context',
        label: t('disaEntries:columns.context'),
        visible: true,
        sortable: true
      },
      {
        key: 'classOfServiceName',
        label: t('disaEntries:columns.classOfService'),
        visible: true,
        sortable: false
      },
      {
        key: 'enabled',
        label: t('disaEntries:columns.enabled'),
        visible: true,
        sortable: true
      },
      { key: 'actions', label: t('disaEntries:columns.actions'), visible: true, sortable: false }
    ];
  }, [t]);

  const dataGrid = useDataGrid({
    fetchData: fetchEntries,
    initialColumns: getAvailableColumns(),
    initialSortBy: 'name',
    initialSortOrder: 'asc',
    initialLimit: 10,
    moduleName: 'disa-entries'
  });

  // Refresh data when domainId changes
  useEffect(() => {
    if (domainId !== undefined) {
      dataGrid.refresh();
    }
  }, [domainId]);

  return {
    ...dataGrid,
    getAvailableColumns
  };
}

